import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";

import {
  closeSweepPayoutModal,
  payoutSelector,
  setPayout,
} from "@/store/payout.store";
import { useMutation } from "@apollo/client/react";
import { toast } from "react-toastify";
import { NotificationType } from "../../enums/notification.enum.ts";
import NotificationToast from "../toasts/notification.toast.tsx";
import {
  formatCurrencyWithoutSign,
  getHumanDescCurrency,
} from "../../utils/currency.util.ts";
import { getSweepAmounts } from "../../utils/sweep.util.ts";
import { SWEEP_PAYOUT_MUTATION } from "@/api/graphql/mutations/sweep-payout.mutation";
import { SweepPayoutResponse } from "../../types/payment/sweep-payout-response.type.ts";

const SweepPayoutModal: FC = () => {
  const dispatcher: AppDispatch = useDispatch();
  const [show, setShow] = useState<boolean>(false);
  const {
    sweepModal: { payoutId },
    payout,
  } = useSelector(payoutSelector);

  const [sweepPayout, sweepPayoutData] =
    useMutation<SweepPayoutResponse>(SWEEP_PAYOUT_MUTATION);

  const { coin, token } = getSweepAmounts(payout);

  useEffect((): void => {
    if (!payoutId) {
      setShow(false);
    } else {
      setShow(true);
    }
  }, [payoutId]);

  useEffect((): void => {
    if (!show) {
      dispatcher(closeSweepPayoutModal(null));
      dispatcher(setPayout(null));
    }
  }, [dispatcher, show]);

  useEffect(() => {
    if (sweepPayoutData.error) {
      toast(
        <NotificationToast
          type={NotificationType.WARNING}
          title="Payout"
          message={sweepPayoutData.error.message}
        />,
        {
          toastId: "sweep-payout-error",
        },
      );
    }
  }, [sweepPayoutData.error]);

  useEffect(() => {
    if (sweepPayoutData.data) {
      toast(
        <NotificationToast
          type={NotificationType.SUCCESS}
          title="Payout"
          message={sweepPayoutData.data.sweepPayoutOperation.message}
        />,
        {
          toastId: "sweep-payout-success",
        },
      );

      setShow(false);
    }
  }, [sweepPayoutData.data]);

  const accept = useCallback(async () => {
    await sweepPayout({
      variables: {
        operationId: payoutId,
      },
    });
  }, [sweepPayout, payoutId]);

  return (
    <>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-80"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="fixed inset-0 bg-slate-900/30 z-50 transition-opacity"></div>
      </Transition>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-in-out duration-200"
        enterFrom="opacity-0 translate-y-4"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-4"
      >
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-lg overflow-auto max-w-lg w-full max-h-full"
          >
            {/* Modal header */}
            <div className="p-4">
              <div className="text-lg font-semibold text-slate-800">
                Return Funds to Balance Address
              </div>
            </div>
            <div className="divide-y">
              <div></div>
              <div></div>
            </div>
            {/* Modal content */}
            <div className="p-4 pb-0">
              <div className="text-xs italic mb-6 text-center">
                Everything left on this payout's temporary address will be sent
                back to your balance address. The payout itself is not retried.
              </div>

              <div className="p-2">
                <div className="text-sm flex items-center justify-between mr-1 font-medium">
                  <span className="font-bold">Payout amount:</span>
                  {formatCurrencyWithoutSign(payout?.amount || "0")}{" "}
                  {getHumanDescCurrency(payout?.currency)}
                </div>
                <div className="mt-3 border-t pt-3">
                  <div className="text-sm font-bold mb-1">Will be returned</div>
                  {token || coin ? (
                    [token, coin].filter(Boolean).map((balance) => (
                      <div
                        key={balance!.currencyType}
                        className="text-sm flex items-center justify-between mr-1 font-medium"
                      >
                        <span>{getHumanDescCurrency(balance!.currency)}</span>
                        {formatCurrencyWithoutSign(balance!.balance)}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-500">Nothing left</div>
                  )}
                </div>
              </div>
            </div>
            {/* Modal footer */}

            <div className="flex justify-end p-4">
              <button
                onClick={() => setShow(false)}
                className="bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2"
              >
                Cancel
              </button>
              <button
                onClick={accept}
                disabled={sweepPayoutData.loading || (!coin && !token)}
                className={`${sweepPayoutData.loading || (!coin && !token) ? "opacity-40 cursor-not-allowed" : ""} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}
              >
                {sweepPayoutData.loading ? (
                  <svg
                    className="animate-spin w-4 h-4 fill-current shrink-0 mr-2"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                  </svg>
                ) : (
                  ""
                )}
                Return Funds
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default SweepPayoutModal;
